/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  editFieldForceAccount,
  getSpecificFieldForceAccount,
} from "../../../../../redux/features/accounts/fieldForceAccount";
import Loader from "../../../../../components/Loader/Index";
import SmallPopup from "../../../../../components/_utils/CommonSmallPopup";
import AdditionalRecipientCreate from "../../../../../components/AdditionalRecipientCreate";
import PrimaryStepper from "../../../../../components/_utils/CommonSteppers/PrimaryStepper";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import TopNavComponent from "../../../../../components/_utils/CommonTopNav/TopNavComponent";
import AdditionalRecipients from "../../../../../components/_utils/AccountCreationComponents/AdditionalRecipients";

const normalizeRecipients = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value) return [];
  return value
    .split(",")
    .map((recipient) => recipient.trim())
    .filter(Boolean);
};

const FieldForceAccountEditRecipients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {
    getSpecificFieldForceAccountData,
    getSpecificFieldForceAccountLoading,
    editFieldForceAccountLoading,
  } = useSelector((state) => state.fieldForceAccount);

  const [recipients, setRecipients] = useState([]);
  const [tempRecipients, setTempRecipients] = useState([]);
  const [initialRecipients, setInitialRecipients] = useState([]);
  const [popupOriginalRecipients, setPopupOriginalRecipients] = useState([]);
  const [recipientButtonDisabled, setRecipientButtonDisabled] = useState(true);
  const [showAdditionalSection, setShowAdditionalSection] = useState(false);
  const [openPopEdit, setOpenPopEdit] = useState(false);
  const [additionalChip, setAdditionalChip] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchedAccount =
    getSpecificFieldForceAccountData?.field_force_accounts?.[0];
  const account =
    String(fetchedAccount?.id) === String(id) ? fetchedAccount : null;
  const isUpdateDisabled =
    JSON.stringify(recipients) === JSON.stringify(initialRecipients);
  const isAccountLoading = getSpecificFieldForceAccountLoading !== false;

  useEffect(() => {
    dispatch(getSpecificFieldForceAccount({ id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (!account) return;
    const accountRecipients = normalizeRecipients(account.recipient_email);
    const draftRecipients = normalizeRecipients(
      location.state?.recipients ?? accountRecipients,
    );
    setRecipients(draftRecipients);
    setTempRecipients(draftRecipients);
    setInitialRecipients(accountRecipients);
    setPopupOriginalRecipients(draftRecipients);
  }, [account?.id, account?.recipient_email, location.state?.recipients]);

  useEffect(() => {
    setRecipientButtonDisabled(
      JSON.stringify(tempRecipients) ===
        JSON.stringify(popupOriginalRecipients),
    );
  }, [tempRecipients, popupOriginalRecipients]);

  const handleConfirmRecipients = () => {
    if (recipientButtonDisabled) return;
    setRecipients(tempRecipients);
    setShowAdditionalSection(false);
  };

  const openRecipientEditor = () => {
    setAdditionalChip([]);
    setTempRecipients(recipients);
    setPopupOriginalRecipients(recipients);
    setRecipientButtonDisabled(true);
    setShowAdditionalSection(true);
  };

  const handleUpdateRecipients = async () => {
    if (isUpdateDisabled || isUpdating || !account) return;

    setIsUpdating(true);
    const response = await dispatch(
      editFieldForceAccount({
        id,
        recipient_email: recipients.join(","),
        linked_user_id: account.field_force_user_id || account.linked_user_id,
      }),
    );
    setIsUpdating(false);

    if (response?.payload?.status === true) {
      navigate(
        `/field_force_account_dashboard/field_force_account_view_all/field_force_account_detail_page/edit_recipients_success/${id}`,
        { replace: true, state: { recipients } },
      );
      return;
    }

    const errorPayload = response?.payload;
    const errorMessage =
      (typeof errorPayload === "string" && errorPayload) ||
      errorPayload?.errors?.field_force_accounts?.[0] ||
      errorPayload?.errors?.field_force_account?.[0] ||
      errorPayload?.errors?.base?.[0] ||
      response?.error?.message ||
      "Unable to update recipients. Please try again.";
    navigate(
      `/field_force_account_dashboard/field_force_account_view_all/field_force_account_detail_page/edit_recipients_error/${id}`,
      { replace: true, state: { errorMessage, recipients } },
    );
  };

  const handleBack = () => {
    if (!isUpdateDisabled) {
      setOpenPopEdit(true);
    } else {
      navigate(-1);
    }
  };

  const handleLeave = () => {
    setOpenPopEdit(false);
    navigate(-1);
  };

  const dataArray = [
    {
      previousText: "Cancel",
      nextText: "Update",
      nextHoverOption: "black",
      previousEnableBgColor: "var(--primary-nav-act)",
      nextEnableBgColor: "var(--positive-act)",
      previousDisableBgColor: "var(--primary-nav-act)",
      nextDisableBgColor: "var(--text-disabled)",
      nextIsEnabled:
        !isUpdateDisabled &&
        !isUpdating &&
        !getSpecificFieldForceAccountLoading,
      stepperText: "Recipients",
      nextIsLoading: isUpdating || editFieldForceAccountLoading,
      nextLoaderNeeded: true,
    },
  ];

  if (isAccountLoading) {
    return (
      <div className="min-h-screen w-width-full bg-primary-bg flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="edit-1024-con bg-primary-bg">
      <div
        className="edit-category-main"
        style={{
          overflowY: "scroll",
          scrollbarWidth: "none",
        }}
      >
        {openPopEdit && (
          <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
            <SmallPopup
              headerTitle="Confirm leaving this page?"
              headerFontWeight="semibold"
              headerFontColor="text-primary"
              textSize="font-md"
              subtext="Leaving this page will not update the changes made."
              btnOneText="Stay"
              btnOneBtnType="button"
              btnOneEnabledBgColor="transparent"
              btnOneHandleFunction={() => setOpenPopEdit(false)}
              btnOneOutLineColor="var(--text-primary)"
              btnOneEnabledTextColor="var(--text-primary)"
              btnOneIsDisabled={false}
              btnOneHoverOption="white"
              btnTwoText="Leave"
              btnTwoBtnType="button"
              btnTwoHandleFunction={handleLeave}
              btnTwoEnabledBgColor="var(--negative-act)"
              btnTwoEnabledTextColor="var(--text-primary)"
              btnTwoIsDisabled={false}
              btnTwoHoverOption="black"
            />
          </div>
        )}

        {showAdditionalSection && (
          <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
            <AdditionalRecipientCreate
              finalRecipients={initialRecipients}
              initialRecipientCount={initialRecipients.length}
              setShowAdditionalSection={setShowAdditionalSection}
              handleConfirm={handleConfirmRecipients}
              recipients={tempRecipients}
              setRecipients={setTempRecipients}
              buttonDisabled={recipientButtonDisabled}
              setButtonDisabled={setRecipientButtonDisabled}
              additionalChip={additionalChip}
              setAdditionalChip={setAdditionalChip}
            />
          </div>
        )}

        <div className="h-navbar-breadcrumb-height hidden Qmd:block" />
        <div className="main_edit_category_stepper_div Qmd:relative">
          <PrimaryStepper
            activeStep={GlobalConfiguration.lengthCheckZero}
            handleForwardAction={handleUpdateRecipients}
            handleBackwardAction={handleBack}
            dataArray={dataArray}
          />
        </div>
        <div className="w-width-full relative Qmd:hidden block">
          <TopNavComponent
            isFormattingNotRequired
            topNavSubText="Recipients"
            topNavHeader="Field force account"
            topNavCtaBtn1Props={{
              btnText: "Update",
              btnType: "button",
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              inActiveVariantNeeded: true,
              disabledBgColor: "var(--text-disabled)",
              isDisabled: !(
                !isUpdateDisabled &&
                !isUpdating &&
                !getSpecificFieldForceAccountLoading
              ),
              handleFunction:
                !isUpdateDisabled &&
                !isUpdating &&
                !getSpecificFieldForceAccountLoading
                  ? handleUpdateRecipients
                  : () => {},
            }}
          />
        </div>
        <div className="px-padding-md lg:px-padding-base mt-margin-base Qsmd:mt-margin-xl pt-padding-base Qsmd:pt-padding-xl">
          <div className="w-width-full Qlg:w-width-md">
            <div className="flex flex-col Qsmd:pt-padding-md">
              <div className="mt-margin-lg Qmd:mt-margin-base rounded-border-radius-md">
                <AdditionalRecipients
                  setShowAdditionalSection={openRecipientEditor}
                  recipients={recipients}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldForceAccountEditRecipients;
